import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  CheckIcon,
  GiftIcon,
  HourglassIcon,
  PrinterIcon,
  SpinnerGapIcon,
  WarningCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { paymentService, rewardService } from '../../../shared/utils';
import type { Payment } from '../../../shared/utils/paymentTypes';
import { Breadcrumb } from '../components/Breadcrumb';
import { PaymentStatusTag } from '../components/PaymentStatusTag';
import { formatDate, formatMoney, maskIban } from '../utils/format';
import './CallbackPage.css';

interface PaymentStatusResponse extends Payment {
  provider?: {
    paymentScheme?: string;
    providerName?: string;
    providerStatus?: string;
    statusMessage?: string;
  };
}

interface CallbackPageProps {
  paymentRequestId?: string;
  // Sent instead of paymentRequestId when BANKSapi does its own top-level bank
  // redirect (e.g. N26 SCA) straight back to our callbackUrl.
  sessionId?: string;
}

// BANKSapi's own docs: "Poll this endpoint if transactionStatus is not yet SUCCESS or ERROR."
// Delays (ms) between successive checks while status remains non-terminal.
const POLL_DELAYS_MS = [
  2000, 3000, 3000, 4000, 4000, 5000, 5000, 5000, 5000, 5000,
];
const TERMINAL_STATUSES = ['EXECUTED', 'FAILED', 'CANCELLED'];

export const CallbackPage = ({
  paymentRequestId,
  sessionId,
}: CallbackPageProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState<PaymentStatusResponse | null>(null);
  const [rewardEligible, setRewardEligible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const checkRewardEligibility = async (
      executedPayment: PaymentStatusResponse
    ) => {
      try {
        const eligibility = await rewardService.checkEligibility(
          executedPayment.id
        );
        if (!cancelled && eligibility.success && eligibility.data?.eligible) {
          setRewardEligible(true);
        }
      } catch {
        // Non-critical — rewards section simply stays hidden
      }
    };

    const poll = async (attempt: number) => {
      if (!paymentRequestId && !sessionId) {
        console.error(
          'CallbackPage: neither payment_request_id nor sessionId in URL search params'
        );
        setError('No payment request ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = paymentRequestId
          ? await paymentService.getPaymentStatus(paymentRequestId)
          : await paymentService.getPaymentStatusBySessionId(sessionId!);
        if (cancelled) return;

        if (response.success && response.data) {
          setPayment(response.data);
          setLoading(false);

          if (response.data.status === 'EXECUTED') {
            await checkRewardEligibility(response.data);
            return;
          }

          // Keep polling until we reach a terminal status or run out of attempts
          if (
            !TERMINAL_STATUSES.includes(response.data.status) &&
            attempt < POLL_DELAYS_MS.length
          ) {
            timeoutId = setTimeout(
              () => poll(attempt + 1),
              POLL_DELAYS_MS[attempt]
            );
          }
        } else {
          setError(response.error || 'Failed to retrieve payment status');
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        setLoading(false);
      }
    };

    poll(0);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [paymentRequestId, sessionId]);

  const lang = i18n.language;
  const breadcrumb = (
    <Breadcrumb
      items={[
        { label: t('app.nav.payRent'), href: '/pay-rent' },
        { label: t('paymentResult.breadcrumb') },
      ]}
    />
  );

  const goToDashboard = () => navigate({ to: '/' });
  const goToPayRent = () => navigate({ to: '/pay-rent' });
  const handleChooseRewards = () => {
    // Use the internal payment ID, not the payment_request_id
    navigate({ to: '/rewards', search: { payment_id: payment?.id } });
  };

  if (loading) {
    return (
      <>
        {breadcrumb}
        <section className="mc-card mc-state">
          <div className="mc-state__icon">
            <SpinnerGapIcon size={46} weight="bold" className="mc-spin" />
          </div>
          <h2>{t('paymentResult.loadingTitle')}</h2>
          <p>{t('paymentResult.loadingText')}</p>
        </section>
      </>
    );
  }

  if (error || !payment) {
    return (
      <>
        {breadcrumb}
        <section className="mc-card mc-state">
          <div className="mc-state__icon mc-state__icon--danger">
            <WarningCircleIcon size={46} weight="fill" />
          </div>
          <h2>{t('paymentResult.errorTitle')}</h2>
          <p>{error}</p>
          <div className="mc-actions">
            <button
              type="button"
              className="mc-btn mc-btn--outline"
              onClick={goToDashboard}
            >
              {t('paymentResult.goToDashboard')}
            </button>
            <button
              type="button"
              className="mc-btn mc-btn--primary"
              onClick={goToPayRent}
            >
              {t('paymentResult.tryAgain')}
            </button>
          </div>
        </section>
      </>
    );
  }

  // Only an explicit EXECUTED status may render as success. AUTHORIZED (bank
  // consent given) and any unrecognized status stay pending.
  const isExecuted = payment.status === 'EXECUTED';
  const isFailed = payment.status === 'FAILED';
  const isCancelled = payment.status === 'CANCELLED';

  const state = isExecuted
    ? {
        tone: 'success',
        icon: <CheckIcon size={46} weight="bold" />,
        title: t('paymentResult.successTitle'),
        text: t('paymentResult.successText'),
      }
    : isFailed || isCancelled
      ? {
          tone: 'danger',
          icon: <XCircleIcon size={46} weight="fill" />,
          title: t(
            isFailed
              ? 'paymentResult.failedTitle'
              : 'paymentResult.cancelledTitle'
          ),
          text: t(
            isFailed
              ? 'paymentResult.failedText'
              : 'paymentResult.cancelledText'
          ),
        }
      : {
          tone: 'pending',
          icon: <HourglassIcon size={46} weight="bold" />,
          title: t('paymentResult.pendingTitle'),
          text: t('paymentResult.pendingText'),
        };

  return (
    <>
      {breadcrumb}

      <section className="mc-card mc-state callback__state">
        <div className={`mc-state__icon mc-state__icon--${state.tone}`}>
          {state.icon}
        </div>
        <h2>{state.title}</h2>
        <p>{state.text}</p>
        {isExecuted && !!payment.pointsEarned && payment.pointsEarned > 0 && (
          <div className="callback__points">
            <GiftIcon size={18} weight="fill" />
            {t('paymentResult.pointsEarned', { points: payment.pointsEarned })}
          </div>
        )}
        <div className="mc-actions no-print">
          <button
            type="button"
            className="mc-btn mc-btn--outline"
            onClick={goToDashboard}
          >
            {t('paymentResult.goToDashboard')}
          </button>
          <button
            type="button"
            className={`mc-btn ${isExecuted && rewardEligible ? 'mc-btn--soft' : 'mc-btn--primary'}`}
            onClick={goToPayRent}
          >
            {isFailed || isCancelled
              ? t('paymentResult.tryAgain')
              : t('paymentResult.payAgain')}
          </button>
        </div>
      </section>

      {rewardEligible && (
        <section className="mc-card mc-hero callback__rewards no-print">
          <div>
            <h2>{t('paymentResult.rewardsTitle')}</h2>
            <p>{t('paymentResult.rewardsText')}</p>
            <button
              type="button"
              className="mc-btn mc-btn--white"
              onClick={handleChooseRewards}
            >
              <GiftIcon size={20} weight="fill" />
              {t('paymentResult.chooseRewards')}
            </button>
          </div>
          <GiftIcon size={110} weight="duotone" className="mc-hero__art" />
        </section>
      )}

      <section className="mc-card callback__details">
        <div className="mc-row">
          <h2>{t('paymentResult.details')}</h2>
          <button
            type="button"
            className="mc-btn mc-btn--soft mc-btn--small no-print"
            onClick={() => window.print()}
          >
            <PrinterIcon size={16} />
            {t('paymentResult.print')}
          </button>
        </div>
        <div className="mc-data">
          <div>
            <span className="mc-data__label">{t('app.table.amount')}</span>
            <strong className="callback__amount">
              {formatMoney(payment.amount, payment.currency, lang)}
            </strong>
          </div>
          <div>
            <span className="mc-data__label">{t('app.table.recipient')}</span>
            <strong>{payment.recipientName}</strong>
            <div className="mc-muted mc-small">
              {maskIban(payment.recipientIban)}
            </div>
          </div>
          <div>
            <span className="mc-data__label">{t('app.table.status')}</span>
            <PaymentStatusTag status={payment.status} />
          </div>
          <div>
            <span className="mc-data__label">{t('app.table.reference')}</span>
            <strong>{payment.reference}</strong>
          </div>
          <div>
            <span className="mc-data__label">{t('app.table.date')}</span>
            <strong>{formatDate(payment.createdAt, lang, true)}</strong>
          </div>
          <div>
            <span className="mc-data__label">
              {t('paymentResult.paymentId')}
            </span>
            <strong className="callback__id">{payment.id}</strong>
          </div>
        </div>
      </section>
    </>
  );
};
